import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";
import { Camera, Eye, EyeOff, Loader2, Lock, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em]";
const GOLD = "#C9A227";
const CHOCOLATE = "#3D2817";

const fieldClass =
    "h-10 border-[#E8E4DE] bg-[#FFFEF9] text-sm focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";
const primaryBtn =
    "h-10 bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]";
const ghostBtn =
    "h-10 border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const ROLE_STYLES = {
    owner: { bg: "#FDF3DC", text: "#9A7B10" },
    manager: { bg: "#FDE8EC", text: "#4A0E17" },
    stylist: { bg: "#E0EDFA", text: "#3B82C4" },
    therapist: { bg: "#E0EDFA", text: "#3B82C4" },
    receptionist: { bg: "#FBE4EF", text: "#D9468F" },
};

const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_MB = 2;

function validPhone(v) {
    const raw = (v || "").trim();
    if (!raw) return true;
    const digits = raw.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 13;
}

function RoleBadge({ role }) {
    const { bg, text } = ROLE_STYLES[role] ?? { bg: "#FAF9F6", text: CHOCOLATE };
    return (
        <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ background: bg, color: text }}
        >
            {role}
        </span>
    );
}

function Field({ label, hint, error, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-bold uppercase tracking-wide text-[#8A8178]">
                {label}
            </Label>
            {children}
            {error && <p className="text-[11px] font-medium text-[#B8859C]">{error}</p>}
            {hint && !error && <p className="text-[11px] font-medium text-[#8A8178]">{hint}</p>}
        </div>
    );
}

function PasswordInput({ id, value, onChange, placeholder, autoComplete }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Input
                id={id}
                type={show ? "text" : "password"}
                className={`pr-10 ${fieldClass}`}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            <button
                type="button"
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8178] transition-colors hover:text-[#3D2817]"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
            >
                {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
        </div>
    );
}

export default function ProfileAccountDialog({ open, onOpenChange, user, onUserUpdated }) {
    const fileRef = useRef(null);
    const [displayUser, setDisplayUser] = useState(user);
    const [profile, setProfile] = useState({ full_name: "", mobile: "" });
    const [passwords, setPasswords] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });
    const [profileErrors, setProfileErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    useEffect(() => {
        setDisplayUser(user);
    }, [user]);

    useEffect(() => {
        if (!open || !user) return;
        setDisplayUser(user);
        setProfile({ full_name: user.full_name || "", mobile: user.mobile || "" });
        setPasswords({ current_password: "", new_password: "", confirm_password: "" });
        setProfileErrors({});
        setPasswordErrors({});
    }, [open, user]);

    const profileDirty = useMemo(() => {
        if (!user) return false;
        return profile.full_name !== (user.full_name || "")
            || profile.mobile !== (user.mobile || "");
    }, [profile, user]);

    const passwordTouched = passwords.current_password
        || passwords.new_password
        || passwords.confirm_password;

    const hasPhoto = !!displayUser?.avatar_url;

    const validateProfile = () => {
        const errs = {};
        if (!profile.full_name?.trim()) errs.full_name = "Name is required";
        if (!validPhone(profile.mobile)) errs.mobile = "Enter a valid phone (10–13 digits)";
        setProfileErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validatePassword = () => {
        const errs = {};
        if (!passwords.current_password) errs.current_password = "Required";
        if (!passwords.new_password) errs.new_password = "Required";
        else if (passwords.new_password.length < 8) errs.new_password = "Min 8 characters";
        if (!passwords.confirm_password) errs.confirm_password = "Required";
        else if (passwords.new_password !== passwords.confirm_password) {
            errs.confirm_password = "Passwords do not match";
        }
        setPasswordErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const applyUser = (data) => {
        setDisplayUser(data);
        onUserUpdated?.(data);
    };

    const saveProfile = async () => {
        if (!validateProfile()) return;
        setSavingProfile(true);
        try {
            const { data } = await api.patch("/auth/me", {
                full_name: profile.full_name.trim(),
                mobile: profile.mobile.trim(),
            });
            applyUser(data);
            toast.success("Profile saved");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Could not save profile");
        } finally {
            setSavingProfile(false);
        }
    };

    const savePassword = async () => {
        if (!validatePassword()) return;
        setSavingPassword(true);
        try {
            await api.patch("/auth/me/password", {
                current_password: passwords.current_password,
                new_password: passwords.new_password,
            });
            toast.success("Password updated");
            setPasswords({ current_password: "", new_password: "", confirm_password: "" });
            setPasswordErrors({});
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Could not update password");
        } finally {
            setSavingPassword(false);
        }
    };

    const onPickPhoto = () => fileRef.current?.click();

    const onPhotoSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!ACCEPT.split(",").includes(file.type)) {
            toast.error("Use JPG, PNG, or WebP");
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Image must be under ${MAX_MB} MB`);
            return;
        }
        setUploadingPhoto(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/auth/me/avatar", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            applyUser(data);
            toast.success("Photo updated");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Could not upload photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const removePhoto = async () => {
        setUploadingPhoto(true);
        try {
            const { data } = await api.delete("/auth/me/avatar");
            applyUser(data);
            toast.success("Photo removed");
        } catch {
            toast.error("Could not remove photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="gap-0 overflow-hidden rounded-xl border-[#E8E4DE] bg-white p-0 font-['Outfit',system-ui,sans-serif] shadow-[0_16px_48px_rgba(45,41,38,0.12)] sm:max-w-[440px]"
                data-testid="profile-account-dialog"
            >
                <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/90" aria-hidden />

                <DialogHeader className="space-y-0 px-6 pb-0 pt-6">
                    <p className={EYEBROW} style={{ color: GOLD }}>Account</p>
                    <DialogTitle className="mt-1 text-lg font-bold" style={{ color: CHOCOLATE }}>
                        My profile
                    </DialogTitle>
                </DialogHeader>

                <div className="scrollbar-dialog max-h-[min(80vh,640px)] overflow-y-auto overscroll-contain px-6 py-5 pr-5 pb-6">
                    {/* Photo */}
                    <section className="mb-6 flex flex-col items-center text-center">
                        <div className="relative">
                            <UserAvatar user={displayUser} size="xl" className="shadow-md" />
                            <button
                                type="button"
                                disabled={uploadingPhoto}
                                onClick={onPickPhoto}
                                className="absolute bottom-0 right-0 flex size-8 items-center justify-center rounded-full border-2 border-white bg-[#4A0E17] text-white shadow-md transition-transform hover:scale-105 disabled:opacity-60"
                                aria-label="Upload profile photo"
                                data-testid="profile-upload-photo"
                            >
                                {uploadingPhoto ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                    <Camera className="size-3.5" />
                                )}
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept={ACCEPT}
                                className="sr-only"
                                onChange={onPhotoSelected}
                            />
                        </div>
                        <p className="mt-3 text-sm font-semibold text-[#2D2926]">
                            {displayUser?.full_name || "User"}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                            <RoleBadge role={displayUser?.role || "staff"} />
                            {displayUser?.tenant_name && (
                                <span className="text-[11px] font-medium text-[#9A7B10]">
                                    {displayUser.tenant_name}
                                </span>
                            )}
                        </div>
                        <p className="mt-2 text-[11px] text-[#8A8178]">
                            JPG, PNG or WebP · max {MAX_MB} MB · optional
                        </p>
                        <div className="mt-3 flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={ghostBtn}
                                disabled={uploadingPhoto}
                                onClick={onPickPhoto}
                            >
                                {hasPhoto ? "Change photo" : "Upload photo"}
                            </Button>
                            {hasPhoto && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-9 border-[#E8E4DE] text-[#B8859C] hover:bg-[#FEF5F3] hover:text-[#4A0E17]"
                                    disabled={uploadingPhoto}
                                    onClick={removePhoto}
                                    data-testid="profile-remove-photo"
                                >
                                    <Trash2 className="mr-1 size-3.5" />
                                    Remove
                                </Button>
                            )}
                        </div>
                    </section>

                    <Separator className="mb-6 bg-[#E8E4DE]" />

                    {/* Personal */}
                    <section className="space-y-3.5">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A8178]">
                            Personal details
                        </p>
                        <Field label="Full name" error={profileErrors.full_name}>
                            <Input
                                className={fieldClass}
                                value={profile.full_name}
                                onChange={(e) => {
                                    setProfile({ ...profile, full_name: e.target.value });
                                    if (profileErrors.full_name) {
                                        setProfileErrors((p) => ({ ...p, full_name: undefined }));
                                    }
                                }}
                                placeholder="Your name"
                                data-testid="profile-dialog-name"
                            />
                        </Field>
                        <Field label="Mobile" error={profileErrors.mobile}>
                            <div className="relative">
                                <Phone className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                <Input
                                    className={`pl-9 ${fieldClass}`}
                                    value={profile.mobile}
                                    onChange={(e) => {
                                        setProfile({ ...profile, mobile: e.target.value });
                                        if (profileErrors.mobile) {
                                            setProfileErrors((p) => ({ ...p, mobile: undefined }));
                                        }
                                    }}
                                    placeholder="+91 98765 43210"
                                    data-testid="profile-dialog-mobile"
                                />
                            </div>
                        </Field>
                        <Field label="Email" hint="Login email — contact support to change">
                            <div className="relative">
                                <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                <Input
                                    className={`pl-9 ${fieldClass}`}
                                    readOnly
                                    value={displayUser?.email || ""}
                                />
                            </div>
                        </Field>
                        {profileDirty && (
                            <Button
                                type="button"
                                className={`w-full ${primaryBtn}`}
                                disabled={savingProfile}
                                onClick={saveProfile}
                                data-testid="profile-dialog-save"
                            >
                                {savingProfile ? "Saving…" : "Save changes"}
                            </Button>
                        )}
                    </section>

                    <Separator className="my-6 bg-[#E8E4DE]" />

                    {/* Password */}
                    <section className="space-y-3.5">
                        <div className="flex items-center gap-2">
                            <Lock className="size-3.5 text-[#D4AF37]" strokeWidth={2.5} />
                            <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A8178]">
                                Change password
                            </p>
                        </div>
                        <Field label="Current password" error={passwordErrors.current_password}>
                            <PasswordInput
                                id="current-password"
                                value={passwords.current_password}
                                onChange={(e) => {
                                    setPasswords({ ...passwords, current_password: e.target.value });
                                    if (passwordErrors.current_password) {
                                        setPasswordErrors((p) => ({ ...p, current_password: undefined }));
                                    }
                                }}
                                placeholder="••••••••"
                                autoComplete="current-password"
                            />
                        </Field>
                        <div className="grid gap-3.5 sm:grid-cols-2">
                            <Field label="New password" error={passwordErrors.new_password} hint="Min 8 chars">
                                <PasswordInput
                                    id="new-password"
                                    value={passwords.new_password}
                                    onChange={(e) => {
                                        setPasswords({ ...passwords, new_password: e.target.value });
                                        if (passwordErrors.new_password) {
                                            setPasswordErrors((p) => ({ ...p, new_password: undefined }));
                                        }
                                    }}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                />
                            </Field>
                            <Field label="Confirm" error={passwordErrors.confirm_password}>
                                <PasswordInput
                                    id="confirm-password"
                                    value={passwords.confirm_password}
                                    onChange={(e) => {
                                        setPasswords({ ...passwords, confirm_password: e.target.value });
                                        if (passwordErrors.confirm_password) {
                                            setPasswordErrors((p) => ({ ...p, confirm_password: undefined }));
                                        }
                                    }}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                />
                            </Field>
                        </div>
                        {passwordTouched && (
                            <Button
                                type="button"
                                variant="outline"
                                className={`w-full ${ghostBtn}`}
                                disabled={savingPassword}
                                onClick={savePassword}
                                data-testid="profile-dialog-change-password"
                            >
                                {savingPassword ? "Updating…" : "Update password"}
                            </Button>
                        )}
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
}
